import { combine } from '@atlaskit/pragmatic-drag-and-drop/combine';
import {
  draggable,
  dropTargetForElements,
} from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import {
  attachClosestEdge,
  extractClosestEdge,
} from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge';
import { type Edge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/types';
import { DropIndicator } from '@atlaskit/pragmatic-drag-and-drop-react-drop-indicator/box-without-terminal';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { actionBarState } from '@databases/action-bar/action-bar-state';
import SelectFieldMenu from '@databases/context-menu/select-field-menu';
import { Position } from '@databases/context-menu/type';
import { useContextualMenu } from '@databases/contextual-menu';
import { aiFirstCreationStreamingState } from '@databases/database-ai-first-creation-state';
import {
  BORDER_BLUE,
  BORDER_GREEN,
  BORDER_ORANGE,
  BORDER_PURPLE,
  useDatabaseStreamState,
} from '@databases/database-ai-first-creation-ui';
import { DragType, setPublicDragData } from '@databases/drag';
import FieldNameWithActions, {
  type FieldNameWithActionsProps,
} from '@databases/field-name/FieldNameWithActions';
import Z_INDEX from '@databases/layout/z-index';
import { PresenceNameplate, usePresence } from '@databases/presence';
import { getGridTemplateColumns } from '@databases/quick-calc/utils';
import { isModifierKeyPressed, useHoverState } from '@databases/react';
import { type DefaultRootState, defaultStoreUtils } from '@databases/redux';
import {
  selectFieldMulti,
  selectFieldSingle,
  setSoftSelection,
} from '@databases/redux/cellSelections/actions';
import {
  selectIsEntireFieldSelected,
  selectIsHoverDeleteFieldsButton,
  selectIsSoftSelectEnabled,
  selectSelectedAdjacentCells,
  selectSelectedEntireFieldIds,
} from '@databases/redux/cellSelections/selectors';
import { TABLE_CLASSNAMES } from '@databases/utils/constants';
import { blurActiveElement, removeTextSelection } from '@databases/utils/database/utils';
import { useIsFeatureEnabled } from '@databases/utils/hooks/useIsFeatureEnabled';
import useView from '@databases/views/useView';
import composeRefs from '@seznam/compose-react-refs';
import classNames from 'classnames';
import React, {
  type ForwardedRef,
  forwardRef,
  type RefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import styled, { css } from 'styled-components';
import { useSnapshot } from 'valtio';

import { getExperimentValue, isFeatureFlagEnabled } from '@canvas/client-feature-flags';

import {
  getCellsFromRow,
  getRowFromCell,
  getRowsFromBody,
  getTableFromBodyRow,
} from '../../../table';
import { isApple } from '../../../utils';

import AddFieldDot from './AddFieldDot';
import { type DragState } from './TableLayoutRow';

const SELECT_BUTTON_HEIGHT = 14;
const DROP_INDICATOR_WIDTH = 4;

const DropIndicatorStyling = css<{ containerHeight: number }>`
  ::after {
    content: ' ';
    position: absolute;
    opacity: 1;
    top: ${token('space.negative.050', '-4px')};
    left: ${token('space.negative.025', '-2px')};
    width: ${DROP_INDICATOR_WIDTH}px;
    height: ${({ containerHeight }) => `${containerHeight - 8}px`};
    border-radius: ${token('radius.xsmall', '2px')};
    border: none;
    background-color: ${colors.B200};
    transition: opacity 0.2s;
    z-index: ${Z_INDEX.TABLE_VIEW_DROP_INDICATOR};
  }
`;

const PragmaticDropIndicatorStyling = styled.div<{ containerHeight: number }>`
  content: ' ';
  position: absolute;
  opacity: 1;
  left: ${token('space.negative.025', '-2px')};
  height: ${({ containerHeight }) => `${containerHeight - 8}px`};
  border-radius: ${token('radius.xsmall', '2px')};
  border: none;
  background-color: ${colors.B200};
  transition: opacity 0.2s;
  z-index: ${Z_INDEX.TABLE_VIEW_DROP_INDICATOR};
`;

// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
const PragmaticDropIndicatorStylingLastInRow = styled.div<{ containerHeight: number }>`
  ::after {
    content: ' ';
    position: absolute;
    opacity: 1;
    width: 2px;
    right: -1px;
    height: ${({ containerHeight }) => `${containerHeight - 8}px`};
    border-radius: ${token('radius.xsmall', '2px')};
    border: none;
    background-color: ${colors.B200};
    transition: opacity 0.2s;
    z-index: ${Z_INDEX.TABLE_VIEW_DROP_INDICATOR};
  }
`;

export const Th = styled.th<{
  isDragOver: boolean;
  containerHeight: number;
  isFirstInRow: boolean;
  isVisualRefreshM1Enabled: boolean;
  collaboratorColor?: string;
}>`
  position: relative;
  overflow: visible;
  --angle: 0deg;
  --column-height: 0px;
  --column-left-inset: 0px;
  &.${TABLE_CLASSNAMES.SHOW_AS_DISABLED} {
    * {
      cursor: ${({ isFilterOrSortingActive }) =>
        isFilterOrSortingActive ? 'not-allowed' : 'grabbing'} !important;
    }
  }

  background: ${({ isVisualRefreshM1Enabled }) =>
    isVisualRefreshM1Enabled
      ? token('elevation.surface.sunken')
      : token('elevation.surface', '#FFFFFF')};

  &:hover:not(.${TABLE_CLASSNAMES.SHOW_AS_DISABLED}) {
    .${TABLE_CLASSNAMES.SELECT_BUTTON} {
      display: flex;
    }
  }

  ${({ isDragOver }) => isDragOver && DropIndicatorStyling}
  // This maximizes the table header cell height to the height of the whole table body to be able to drag the column over the whole table
  &.${TABLE_CLASSNAMES.MAXIMIZE_HEIGHT} {
    ::before {
      content: '';
      position: absolute;
      left: 0;
      top: ${token('space.negative.100', '-8px')};
      height: ${({ containerHeight }) => `${containerHeight - 8}px`};
      width: 100%;
    }
  }

  &.${TABLE_CLASSNAMES.IS_ROVO_GRADIENT_SELECTED}::before {
    content: '';
    position: absolute;
    top: ${token('space.negative.025', '-2px')};
    left: ${token('space.negative.025', '-2px')};
    right: ${token('space.negative.025', '-2px')};
    height: var(--column-height, 0px);
    border-radius: ${token('radius.small', '3px')};
    padding: ${token('space.025', '2px')};
    background: conic-gradient(
      from var(--angle, 0deg),
      ${BORDER_ORANGE} 25%,
      ${BORDER_GREEN} 25% 50%,
      ${BORDER_BLUE} 50% 75%,
      ${BORDER_PURPLE} 75% 100%
    );
    -webkit-mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
    pointer-events: none;
    z-index: 1;
  }

  ${({ collaboratorColor }) => {
    // eslint-disable-next-line @atlaskit/design-system/no-unsafe-design-token-usage
    const borderColor = collaboratorColor && token(collaboratorColor);

    return (
      borderColor &&
      css`
        border: ${token('border.width.selected', '2px')} solid ${borderColor};
        border-radius: ${token('radius.small', '3px')};
      `
    );
  }}
`;

type TableLayoutHeaderCellRefObj = RefObject<
  TableLayoutHeaderCellRefs<
    RefObject<HTMLDivElement | null>,
    RefObject<HTMLDivElement | null>
  >
>;

type TableLayoutHeaderCellRefs<H, T> = {
  headerFieldRef: H;
  tableBodyRef: T;
};

export interface TableLayoutHeaderCellProps extends FieldNameWithActionsProps {
  canEditFields: boolean;
  couldEditFields: boolean;
  className?: string;
  currentViewId: string;
  isLastInRow?: boolean;
  onDragStart?: (e: React.DragEvent, entryId: string) => void;
  onDrag?: (e: React.DragEvent, entryId: string) => void;
  onDragEnd?: (e: React.DragEvent, entryId: string) => void;
  isDraggingActive?: boolean;
  isDragOver: boolean;
  containerHeight: number;
  fieldIndex: number;
  fieldToScroll: string | null;
  setFieldToScroll: (fieldId: string | null) => void;
}

/**
 * Renders a th element that contains the field name.
 * The field name can be dragged to reorder the fields.
 */
const TableLayoutHeaderCell = React.memo(
  forwardRef<
    TableLayoutHeaderCellRefs<
      ForwardedRef<HTMLElement | null>,
      ForwardedRef<HTMLTableSectionElement | null>
    >,
    TableLayoutHeaderCellProps
  >(
    (
      {
        canEditFields,
        contentId,
        fieldId,
        className,
        isLastInRow,
        onDragStart,
        onDrag,
        onDragEnd,
        isDraggingActive,
        containerHeight,
        isDragOver,
        currentViewId,
        isFirstInRow,
        fieldIndex,
        fieldToScroll,
        setFieldToScroll,
        ...otherProps
      },
      ref
    ) => {
      const { view } = useView();
      const viewSettings = view.settings;
      const inputRef = useRef<HTMLInputElement>(null);
      const thRef = useRef<HTMLTableCellElement>(null);

      const { isStreaming, operationType } = useDatabaseStreamState();
      const isDBAiFirstCreationEnabled = useIsFeatureEnabled('dbAiFirstCreation');
      const isEditStreaming = operationType === 'edit';

      const { isSingleFieldStreaming } = useSnapshot(aiFirstCreationStreamingState);
      const isAIStreaming = isDBAiFirstCreationEnabled && isStreaming;
      const isFieldEditStreaming = isEditStreaming && Boolean(isSingleFieldStreaming);
      useEffect(() => {
        if (isAIStreaming && !isEditStreaming) {
          setFieldToScroll(null);
          return;
        }

        if (isAIStreaming && isFieldEditStreaming) {
          setFieldToScroll(null);
          return;
        }

        if (fieldToScroll === fieldId && thRef?.current) {
          requestAnimationFrame(() => {
            thRef.current?.scrollIntoView({
              behavior: 'smooth',
              block: 'nearest',
              inline: 'center',
            });
            setFieldToScroll(null);
          });
        }
      }, [
        fieldId,
        fieldToScroll,
        setFieldToScroll,
        isEditStreaming,
        isFieldEditStreaming,
        isAIStreaming,
      ]);

      const [isHoverDragButton, dragButtonHoverProps] = useHoverState();
      const isVisualRefreshM1Enabled = isFeatureFlagEnabled(
        'databases-frontend-visual-refresh-table-m1'
      );

      const actionBar = useSnapshot(actionBarState.state);
      const { isActionBarEnabled } = actionBar;
      const isDatabasePresenceEnabled = getExperimentValue(
        'databasePresence',
        'enabled',
        {
          fireExperimentExposure: false,
        }
      );

      const { participants, recentCollaboratorColor } = usePresence({
        grouping: { fieldId },
        componentId: 'TableLayoutHeaderCell',
        isEnabled: isDatabasePresenceEnabled,
      });

      const [
        isSelected,
        isSoftSelected,
        hoverDeleteButton,
        selectedAdjacentCells,
        selectedColumns,
      ] = defaultStoreUtils.useEqualSelector((state: DefaultRootState) => {
        const isSoftSelected = selectIsSoftSelectEnabled(state);

        const isSelected = selectIsEntireFieldSelected(state, {
          fieldId,
        });

        return [
          isSelected,
          isSoftSelected,
          isSelected && selectIsHoverDeleteFieldsButton(state),
          isSelected
            ? selectSelectedAdjacentCells(state, {
                fieldId,
                contentId,
              })
            : undefined,
          isSelected ? selectSelectedEntireFieldIds(state, { contentId }) : [],
        ] as const;
      });

      const dispatch = defaultStoreUtils.useDispatch();
      const handleSelectColumn = useCallback(
        (e: React.MouseEvent | React.KeyboardEvent) => {
          const isMultiSelectOn = isModifierKeyPressed(e, [
            isApple ? 'metaKey' : 'ctrlKey',
            'shiftKey',
          ]);
          const isRangeSelectOn = isModifierKeyPressed(e, ['shiftKey']);
          // Blur the active input element when the cell selection mode has been 'cell' before
          blurActiveElement();
          removeTextSelection();
          if (isRangeSelectOn) {
            dispatch(
              selectFieldMulti({
                contentId,
                fieldId,
                isRangeSelect: !isMultiSelectOn,
              })
            );
          } else {
            dispatch(
              selectFieldSingle({
                contentId,
                fieldId,
              })
            );
          }
          dispatch(setSoftSelection({ value: false }));
        },
        [contentId, dispatch, fieldId]
      );

      const { closeMenu } = useContextualMenu();

      const [dragState, setDragState] = useState<DragState>({ type: 'idle' });
      const showAsDisabled = isSelected && dragState.type === 'dragging';

      const dragImage = useRef<HTMLElement>();
      const handleDragStart = useCallback(
        (e: React.DragEvent) => {
          if (e.target !== e.currentTarget || !thRef.current) {
            // Ignore dragging descendent elements
            return;
          }
          removeTextSelection();
          closeMenu();

          // do not apply any styles to these references of elements or use it directly for the drag image
          const tr = getRowFromCell(thRef.current);
          const table = getTableFromBodyRow(tr);
          const tbody = (ref as TableLayoutHeaderCellRefObj)?.current?.tableBodyRef
            .current as HTMLTableSectionElement | null;
          const trs = getRowsFromBody(tbody);
          const ths = getCellsFromRow(tr);
          const selectedThs = ths.filter((el) =>
            el.classList.contains(TABLE_CLASSNAMES.IS_SELECTED)
          );
          const currentIndexInSelectedThs = selectedThs.indexOf(thRef.current);
          const selectedThsIndexOffsetWidth = selectedThs
            .slice(0, currentIndexInSelectedThs + 1)
            .reduce((p, c) => p + c.clientWidth, 0);
          const selectedThsWidths = selectedThs.map((el) => el.clientWidth);
          const selectedThsWidth = selectedThsWidths.reduce((p, c) => p + c, 0);

          dragImage.current = document.createElement('div');
          const newThead = document.createElement('thead');
          const newTBody = document.createElement('tbody');
          const newTr = document.createElement('tr');

          // table header copy
          const trClone = tr?.cloneNode(true) as HTMLTableRowElement | null;
          const clonedThs = getCellsFromRow(trClone);
          const clonedSelectedThs = clonedThs.filter((el) =>
            el.classList.contains(TABLE_CLASSNAMES.IS_SELECTED)
          );

          // select handle
          const selectButton = clonedSelectedThs
            .find((el) => el.draggable)
            ?.querySelector(`.${TABLE_CLASSNAMES.SELECT_BUTTON}`) as HTMLElement | null;
          selectButton?.classList.add(TABLE_CLASSNAMES.SHOW_AS_DRAGGING);

          // table body copy
          const tbodyClone = tbody?.cloneNode(true) as HTMLTableSectionElement | null;
          const clonedTrs = getRowsFromBody(tbodyClone);
          clonedTrs?.forEach((el) => {
            const trCopy = newTr.cloneNode(false) as HTMLTableRowElement;
            const clonedThs = getCellsFromRow(el);
            const selectedClonedThs = clonedThs.filter((el) =>
              el.classList.contains(TABLE_CLASSNAMES.IS_SELECTED)
            );
            // eslint-disable-next-line canvas/no-spread-arguments
            trCopy.append(...selectedClonedThs);
            newTBody.append(trCopy);
            Object.assign(trCopy.style, {
              display: 'grid',
              gridTemplateColumns: getGridTemplateColumns(selectedThsWidths),
            });
          });

          // show selected cells as disabled in table body
          trs?.forEach((el) => {
            const ths = getCellsFromRow(el);
            const selectedThs = ths.filter((el) =>
              el.classList.contains(TABLE_CLASSNAMES.IS_SELECTED)
            );
            selectedThs.forEach((el) =>
              el.classList.add(TABLE_CLASSNAMES.SHOW_AS_DISABLED)
            );
          });

          // clone the html table element to get the styles (but do not include descendants)
          const tableClone = table?.cloneNode(false) as HTMLTableElement | null;
          if (tableClone) {
            Object.assign(dragImage.current.style, {
              position: 'absolute',
              top: 0,
              left: 0,
              zIndex: Z_INDEX.DRAG_IMAGE_ELEMENT,
              paddingTop: 0,
            });
            Object.assign(tableClone.style, {
              minWidth: `${selectedThsWidth}px`,
            });
            Object.assign(newTr.style, {
              display: 'grid',
              gridTemplateColumns: getGridTemplateColumns(selectedThsWidths),
            });

            tableClone.append(newThead);
            tableClone.append(newTBody);
            // eslint-disable-next-line canvas/no-spread-arguments
            newTr.append(...clonedSelectedThs);
            newThead.append(newTr);
            dragImage.current.append(tableClone);
            table?.parentElement?.append(dragImage.current);

            e.dataTransfer.setDragImage(
              dragImage.current,
              selectedThsIndexOffsetWidth - (e.target as HTMLElement)?.clientWidth / 2,
              SELECT_BUTTON_HEIGHT / 2
            );
          }

          setPublicDragData(e.dataTransfer, {
            type: DragType.FIELD_COLUMNS,
            contentId,
            draggingIds: selectedColumns,
            dragOverId: fieldId,
            height: thRef.current!.offsetHeight,
            width: thRef.current!.offsetWidth,
          });

          onDragStart?.(e, fieldId);
        },
        [closeMenu, contentId, fieldId, onDragStart, ref, selectedColumns]
      );

      const handleDrag = useCallback(
        (e: React.DragEvent) => {
          if (e.target !== e.currentTarget) {
            // Ignore dragging descendent elements
            return;
          }

          onDrag?.(e, fieldId);
        },
        [fieldId, onDrag]
      );

      const handleDragEnd = useCallback(
        (e: React.DragEvent) => {
          if (e.target !== e.currentTarget) {
            // Ignore dragging descendent elements
            return;
          }

          if (dragImage.current) {
            dragImage.current.remove();
            dragImage.current = undefined;
          }

          const tbody = (ref as TableLayoutHeaderCellRefObj)?.current?.tableBodyRef
            .current as HTMLTableSectionElement | null;
          const trs = getRowsFromBody(tbody);
          // show selected cells as disabled in table body
          trs.forEach((el) => {
            const ths = getCellsFromRow(el);
            const selectedThs = ths.filter((el) =>
              el.classList.contains(TABLE_CLASSNAMES.IS_SELECTED)
            );
            selectedThs.forEach((el) =>
              el.classList.remove(TABLE_CLASSNAMES.SHOW_AS_DISABLED)
            );
          });

          onDragEnd?.(e, fieldId);
        },
        [fieldId, onDragEnd, ref]
      );

      // Pragmatic drag and drop
      useEffect(() => {
        if (!thRef.current) {
          return;
        }
        const element = thRef.current;

        return combine(
          draggable({
            element,
            getInitialData() {
              return { type: DragType.FIELD_COLUMNS, fieldIndex, fieldId };
            },
            onDragStart() {
              setDragState({ type: 'dragging' });
            },
            onDrop() {
              setDragState({ type: 'idle' });
            },
          }),
          dropTargetForElements({
            element,
            getData({ input, element }) {
              const data = { fieldId, fieldIndex };
              return attachClosestEdge(data, {
                input,
                element,
                allowedEdges: ['left', 'right'],
              });
            },
            canDrop({ source }) {
              return (
                (source.data.type === DragType.FIELD_COLUMNS &&
                  source.data.fieldId !== fieldId) ||
                source.data.type === DragType.FILL_VALUES
              );
            },
            onDragEnter(args) {
              if (args.source.data.type === DragType.FIELD_COLUMNS) {
                setDragState({
                  type: 'is-over',
                  closestEdge: extractClosestEdge(args.self.data),
                });
              }
            },
            onDrag(args) {
              if (args.source.data.type === DragType.FIELD_COLUMNS) {
                const closestEdge: Edge | null = extractClosestEdge(args.self.data);

                // only update react state if the `closestEdge` changes
                setDragState((current) => {
                  if (current.type !== 'is-over') {
                    return current;
                  }
                  if (current.closestEdge === closestEdge) {
                    return current;
                  }
                  return {
                    type: 'is-over',
                    closestEdge,
                  };
                });
              }
            },
            onDragLeave() {
              setDragState({ type: 'idle' });
            },
            onDrop() {
              setDragState({ type: 'idle' });
            },
          })
        );
      }, [fieldIndex, fieldId]);

      const shouldShowTelepointer = !isSelected && isDatabasePresenceEnabled;
      const nameplateRef = useRef<HTMLTableCellElement>(null);
      const [isHovering, setIsHovering] = useState(false);
      const handleMouseEnter = useCallback(() => {
        if (!shouldShowTelepointer) {
          return;
        }
        setIsHovering(true);
      }, [shouldShowTelepointer]);

      const handleMouseLeave = useCallback(() => {
        if (!shouldShowTelepointer) {
          return;
        }
        setIsHovering(false);
      }, [shouldShowTelepointer]);

      return (
        <Th
          id={fieldId}
          containerHeight={containerHeight}
          isDragOver={isDragOver}
          draggable={canEditFields && isHoverDragButton}
          onDragStart={handleDragStart}
          onDrag={handleDrag}
          onDragEnd={handleDragEnd}
          isFirstInRow={isFirstInRow}
          data-testid="table-layout-header-cell"
          data-field-id={fieldId}
          data-col-index={fieldIndex}
          ref={composeRefs(
            thRef,
            (ref as TableLayoutHeaderCellRefObj)?.current?.headerFieldRef,
            nameplateRef
          )}
          className={classNames([
            className,
            {
              [TABLE_CLASSNAMES.IS_SELECTED]: isSelected,
              [TABLE_CLASSNAMES.IS_SOFT_SELECTED]: isSoftSelected,
              [TABLE_CLASSNAMES.MAXIMIZE_HEIGHT]: isDraggingActive,
              [TABLE_CLASSNAMES.SHOW_AS_DISABLED]: showAsDisabled,
            },
          ])}
          isVisualRefreshM1Enabled={isVisualRefreshM1Enabled}
          collaboratorColor={recentCollaboratorColor}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          {isHovering && shouldShowTelepointer && !isAIStreaming && (
            <PresenceNameplate
              participants={participants}
              anchorRef={nameplateRef}
              position={'bottom'}
            />
          )}
          {isSelected && !isDraggingActive && !selectedAdjacentCells?.left && (
            <AddFieldDot
              contentId={contentId}
              fieldId={fieldId}
              position={Position.BEFORE}
              canEditFields={canEditFields}
              currentViewId={currentViewId}
            />
          )}
          {canEditFields && dragState.type === 'idle' && (
            <SelectFieldMenu
              contentId={contentId}
              viewSettings={viewSettings}
              currentViewId={currentViewId}
              canEditFields={canEditFields}
              fieldId={fieldId}
              hoverProps={dragButtonHoverProps}
              handleSelectColumn={handleSelectColumn}
              isActionBarEnabled={isActionBarEnabled}
            />
          )}
          <FieldNameWithActions
            contentId={contentId}
            fieldId={fieldId}
            isReadonly={!canEditFields}
            isDragging={showAsDisabled}
            isSelected={isSelected}
            isSoftSelected={isSoftSelected}
            hoverDeleteButton={hoverDeleteButton}
            isLastInRow={isLastInRow}
            selectedAdjacentCells={selectedAdjacentCells}
            ref={inputRef}
            isFirstInRow
            {...otherProps}
          />
          {dragState.type === 'is-over' &&
            dragState.closestEdge &&
            (isLastInRow && dragState.closestEdge == 'right' ? (
              <PragmaticDropIndicatorStylingLastInRow containerHeight={containerHeight}>
                <DropIndicator edge={dragState.closestEdge} />
              </PragmaticDropIndicatorStylingLastInRow>
            ) : (
              <PragmaticDropIndicatorStyling containerHeight={containerHeight}>
                <DropIndicator edge={dragState.closestEdge} />
              </PragmaticDropIndicatorStyling>
            ))}
          {isSelected &&
            !isDraggingActive &&
            !isLastInRow &&
            !selectedAdjacentCells?.right && (
              <AddFieldDot
                contentId={contentId}
                fieldId={fieldId}
                position={Position.AFTER}
                canEditFields={canEditFields}
                currentViewId={currentViewId}
              />
            )}
        </Th>
      );
    }
  )
);

export default TableLayoutHeaderCell;
